import { useQuery } from '@tanstack/react-query';

import React, { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ChevronLeft, ChevronRight, Delete, MoreHoriz } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Menu, MenuItem, Typography } from '@mui/material';

import { useToastService } from '@@common';
import { ConfirmDialog, useNotificationRefetch, useNotifications } from '@@components';
import type { ReleasePlanEventNotification } from '@@components';

import { EnvironmentClient, EnvironmentModel, ReleasePlanModel, ReleasePlanStatus, SearchReleasePlanRequest } from '../../../api/api';
import { formatTimestampSortable } from '../../../common/formatTimestamp';
import useApiClient from '../../../common/hooks/useApiClient';
import SidePanelList, { SidePanelListColumn } from '../../../components/Panels/SidePanelList';
import ReleasePlanStatusChip from './ReleasePlanStatusChip';

interface ReleasePlansPanelProps {
	environment?: EnvironmentModel;
	environments?: EnvironmentModel[];
	onClose: () => void;
	onReleasePlanSelect: (releasePlanId: string) => void;
	onReleasePlanDelete?: (releasePlanId: string) => void;
	selectedReleasePlanId?: string | null;
	refreshTrigger?: number; // External trigger to refresh the data
	onStatusChange?: () => void;
	onCollapseChange?: (collapsed: boolean) => void;
	width: number;
	setWidth: Dispatch<SetStateAction<number>>;
	top?: number;
}

export const RELEASE_PLANS_PANEL_STORAGE_KEY = 'releasePlansPanel.width';
export const RELEASE_PLANS_PANEL_DEFAULT_WIDTH = 500;
export const RELEASE_PLANS_PANEL_MIN_WIDTH = 360;
export const RELEASE_PLANS_PANEL_MAX_WIDTH = 900;
export const RELEASE_PLANS_PANEL_COLLAPSED_WIDTH = 48;

function isReleasePlanListRefreshEvent(event: ReleasePlanEventNotification) {
	return !event.payload.status;
}

export default function ReleasePlansPanel({
	environment,
	environments = [],
	onClose,
	onReleasePlanSelect,
	onReleasePlanDelete,
	selectedReleasePlanId,
	refreshTrigger,
	onStatusChange,
	onCollapseChange,
	width,
	setWidth,
	top = 0,
}: ReleasePlansPanelProps) {
	const client = useApiClient(EnvironmentClient) as EnvironmentClient;
	const { latestReleasePlanEvent } = useNotifications();
	const toastService = useToastService();
	const [refreshKey, setRefreshKey] = useState(0);
	const [deletingPlanId, setDeletingPlanId] = useState<string | null>(null);
	const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ open: boolean; planId: string | null }>({
		open: false,
		planId: null,
	});
	const previousStatusMapRef = useRef<Map<string, ReleasePlanStatus>>(new Map());
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [pageIndex, setPageIndex] = useState(0);
	const pageSize = 25;
	const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);
	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
	const [isBulkDeleting, setIsBulkDeleting] = useState(false);

	const handleToggleCollapse = () => {
		const newCollapsed = !isCollapsed;
		setIsCollapsed(newCollapsed);
		if (onCollapseChange) {
			onCollapseChange(newCollapsed);
		}
	};

	// When no environment is selected, the same endpoint returns plans across all environments.
	const { data, isLoading, isFetching, error, refetch } = useQuery({
		queryKey: ['release-plans', environment?.id ?? 'all', refreshKey, refreshTrigger, pageIndex, pageSize],
		queryFn: async () => {
			const query: SearchReleasePlanRequest = {
				targetEnvironmentId: environment?.id,
				paging: {
					pageIndex,
					pageSize,
					sortBy: 'CreatedDate',
					sortAscending: false, // Most recent first (desc)
				},
			};
			return await client.searchReleasePlan(query);
		},
	});

	const handleRefresh = () => {
		setRefreshKey((prev) => prev + 1);
		refetch();
	};

	// Get release plans array for refresh logic
	const releasePlans = useMemo(() => data?.items || [], [data?.items]);
	const hasNextPage = data?.hasNextPage ?? false;
	const selectedCount = selectedPlanIds.length;
	const isMenuOpen = Boolean(menuAnchorEl);

	// Reset page when environment changes
	useEffect(() => {
		setPageIndex(0);
	}, [environment?.id]);

	// Keep selectedPlanIds in sync with current page items
	const planIds = useMemo(() => new Set(releasePlans.map((p) => p.id)), [releasePlans]);
	useEffect(() => {
		setSelectedPlanIds((prev) => prev.filter((id) => planIds.has(id)));
	}, [planIds]);

	useNotificationRefetch({
		notification: latestReleasePlanEvent,
		getKey: useCallback((event: ReleasePlanEventNotification) => `${event.payload.id}:${event.receivedAt}`, []),
		shouldRefetch: useCallback(
			(event: ReleasePlanEventNotification) => {
				const eventPayload = event.payload;
				if (environment && eventPayload.targetEnvironmentId !== environment.id) {
					return false;
				}
				if (!environment) {
					return true;
				}

				return (
					isReleasePlanListRefreshEvent(event) ||
					planIds.has(eventPayload.id) ||
					eventPayload.id === selectedReleasePlanId ||
					eventPayload.status === ReleasePlanStatus.Pending
				);
			},
			[environment, planIds, selectedReleasePlanId],
		),
		refetch,
	});

	// Track status changes for all release plans and notify parent to refresh environment data
	useEffect(() => {
		const currentStatusMap = new Map<string, ReleasePlanStatus>();
		let hasStatusChanged = false;

		releasePlans.forEach((plan) => {
			if (plan.status !== undefined) {
				currentStatusMap.set(plan.id, plan.status);

				// Check if this plan's status has changed
				const previousStatus = previousStatusMapRef.current.get(plan.id);
				if (previousStatus !== undefined && previousStatus !== plan.status) {
					hasStatusChanged = true;
				}
			}
		});

		if (hasStatusChanged) {
			// At least one status has changed, notify parent to refresh
			onStatusChange?.();
		}

		// Update the ref with current status map
		previousStatusMapRef.current = currentStatusMap;
	}, [releasePlans, onStatusChange]);

	const handleDelete = (planId: string, event: React.MouseEvent) => {
		event.stopPropagation(); // Prevent row selection
		setDeleteConfirmDialog({ open: true, planId });
	};

	const handleConfirmDelete = async () => {
		const { planId } = deleteConfirmDialog;
		if (!planId) return;

		setDeleteConfirmDialog({ open: false, planId: null });
		setDeletingPlanId(planId);

		try {
			await client.deleteReleasePlan(planId);
			// Notify parent component about the deletion to close details panel if needed
			onReleasePlanDelete?.(planId);
			// Refresh the list after successful deletion
			handleRefresh();
		} catch (error) {
			console.error('Error deleting release plan:', error);
			alert('Failed to delete release plan. Please try again.');
		} finally {
			setDeletingPlanId(null);
		}
	};

	const handleCancelDelete = () => {
		setDeleteConfirmDialog({ open: false, planId: null });
	};

	const handleToggleItemSelection = (item: ReleasePlanModel) => {
		setSelectedPlanIds((prev) => (prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]));
	};

	const handleToggleSelectAll = (selected: boolean) => {
		setSelectedPlanIds(selected ? releasePlans.map((p) => p.id) : []);
	};

	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
		setMenuAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setMenuAnchorEl(null);
	};

	const handleOpenBulkDeleteConfirm = () => {
		handleCloseMenu();
		if (selectedCount > 0) {
			setBulkDeleteConfirmOpen(true);
		}
	};

	const handleConfirmBulkDelete = async () => {
		if (selectedPlanIds.length === 0) {
			setBulkDeleteConfirmOpen(false);
			return;
		}

		setIsBulkDeleting(true);
		try {
			await Promise.all(selectedPlanIds.map((id) => client.deleteReleasePlan(id)));
			toastService.success(`Deleted ${selectedPlanIds.length} release plan(s).`);
			selectedPlanIds.forEach((id) => onReleasePlanDelete?.(id));
			setSelectedPlanIds([]);
			setBulkDeleteConfirmOpen(false);
			handleRefresh();
		} catch (err) {
			toastService.error(`Failed to delete release plan(s): ${err instanceof Error ? err.message : 'Unknown error'}`);
		} finally {
			setIsBulkDeleting(false);
		}
	};

	// Define table columns
	const columns: SidePanelListColumn[] = [
		...(!environment ? [{ id: 'environment', label: 'Environment', align: 'left' as const, width: '24%' }] : []),
		{ id: 'created', label: 'Created', align: 'left', width: environment ? '31%' : '26%' },
		{ id: 'status', label: 'Status', align: 'left', width: environment ? '24%' : '20%' },
		{ id: 'steps', label: 'Steps', align: 'center', width: environment ? '14%' : '13%' },
		{ id: 'actions', label: '', align: 'center', width: '17%' },
	];

	// Render cell content based on column
	const renderCell = (plan: ReleasePlanModel, columnId: string): ReactNode => {
		const completedSteps = plan.completedStepCount || 0;
		const totalSteps = plan.steps?.length || 0;

		switch (columnId) {
			case 'environment': {
				const environmentName = environments.find((item) => item.id === plan.targetEnvironmentId)?.name;
				return (
					<Typography variant="body2" sx={{ fontSize: '0.75rem' }} noWrap title={environmentName || plan.targetEnvironmentId || 'Unknown environment'}>
						{environmentName || plan.targetEnvironmentId || 'Unknown'}
					</Typography>
				);
			}
			case 'created':
				return (
					<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
						{formatTimestampSortable(plan.createdDate)}
					</Typography>
				);
			case 'status':
				return <ReleasePlanStatusChip status={plan.status} size="small" />;
			case 'steps':
				return (
					<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
						{completedSteps}/{totalSteps}
					</Typography>
				);
			case 'actions':
				return (
					<IconButton
						size="small"
						onClick={(event) => handleDelete(plan.id, event)}
						disabled={deletingPlanId === plan.id}
						title="Delete release plan"
						sx={{ color: 'error.main' }}
					>
						{deletingPlanId === plan.id ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
					</IconButton>
				);
			default:
				return null;
		}
	};

	const headerActionsContent = (
		<>
			<IconButton size="small" onClick={handleOpenMenu} title="More actions">
				<MoreHoriz />
			</IconButton>
			<Menu anchorEl={menuAnchorEl} open={isMenuOpen} onClose={handleCloseMenu}>
				<MenuItem onClick={handleOpenBulkDeleteConfirm} disabled={selectedCount === 0 || isBulkDeleting}>
					Delete selected ({selectedCount})
				</MenuItem>
			</Menu>
		</>
	);

	const paginationFooter = (
		<Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
			<Typography variant="caption" color="textSecondary">
				Page {pageIndex + 1}
			</Typography>
			<Typography variant="caption" color="textSecondary" sx={{ gridColumn: 2, whiteSpace: 'nowrap' }}>
				{selectedCount > 0 ? `${selectedCount} selected` : ''}
			</Typography>
			<Box sx={{ gridColumn: 3, justifySelf: 'end' }}>
				<IconButton size="small" onClick={() => setPageIndex((p) => Math.max(0, p - 1))} disabled={pageIndex === 0 || isFetching} title="Previous page">
					<ChevronLeft fontSize="small" />
				</IconButton>
				<IconButton size="small" onClick={() => setPageIndex((p) => p + 1)} disabled={!hasNextPage || isFetching} title="Next page">
					<ChevronRight fontSize="small" />
				</IconButton>
			</Box>
		</Box>
	);

	return (
		<>
			<SidePanelList<ReleasePlanModel>
				title="Release Plans"
				subtitle={environment ? `Environment: ${environment.name}` : 'All environments'}
				items={releasePlans}
				columns={columns}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error as Error | null}
				emptyMessage={environment ? 'No release plans found for this environment' : 'No release plans found'}
				selectedItemId={selectedReleasePlanId}
				width={width}
				collapsedWidth={RELEASE_PLANS_PANEL_COLLAPSED_WIDTH}
				isCollapsed={isCollapsed}
				onClose={onClose}
				onRefresh={handleRefresh}
				onItemSelect={(plan) => onReleasePlanSelect(plan.id)}
				renderCell={renderCell}
				getItemId={(plan) => plan.id}
				onToggleCollapse={handleToggleCollapse}
				multiSelectEnabled
				multiSelectColumnWidth={56}
				selectedItemIds={selectedPlanIds}
				onToggleItemSelection={handleToggleItemSelection}
				onToggleSelectAll={handleToggleSelectAll}
				headerActions={headerActionsContent}
				footer={paginationFooter}
				virtualized
				resizeStorageKey={RELEASE_PLANS_PANEL_STORAGE_KEY}
				defaultWidth={RELEASE_PLANS_PANEL_DEFAULT_WIDTH}
				minWidth={RELEASE_PLANS_PANEL_MIN_WIDTH}
				maxWidth={RELEASE_PLANS_PANEL_MAX_WIDTH}
				setWidth={setWidth}
				top={top}
				tableSx={{
					tableLayout: 'fixed',
					'& .MuiTableCell-paddingCheckbox': {
						width: 56,
						minWidth: 56,
						px: 0.5,
						'& .MuiCheckbox-root': {
							p: 0.5,
						},
					},
					'& .MuiTableCell-root': {
						px: 0.75,
						py: 0.75,
					},
				}}
			/>
			{/* Single-item Delete Confirmation Dialog */}
			<ConfirmDialog
				open={deleteConfirmDialog.open}
				title="Delete Release Plan"
				message="Are you sure you want to delete this release plan? This action cannot be undone."
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>
			{/* Bulk Delete Confirmation Dialog */}
			<ConfirmDialog
				open={bulkDeleteConfirmOpen}
				title="Delete Release Plans"
				message={`Are you sure you want to delete ${selectedCount} selected release plan(s)? This action cannot be undone.`}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmBulkDelete}
				onCancel={() => setBulkDeleteConfirmOpen(false)}
			/>
		</>
	);
}
